import { useEffect, useRef } from 'preact/hooks';
import { route } from 'preact-router';
import { models, selectedModel, modelDropdownOpen, activeConv, selectedAgent } from '../pages/ChatHubPage';
import { T } from '../styles/tokens';
import { Button } from './ui';
import { tenantBranding } from '../stores/tenant.store';

interface TopBarProps {
  currentPath: string;
  onHamburgerClick: () => void;
}

export function TopBar({ currentPath, onHamburgerClick }: TopBarProps) {
  const isChatRoute = currentPath === '/chat' || currentPath.startsWith('/chat/');
  const isAgentRoute = currentPath.startsWith('/agents');
  const hasActiveChat = currentPath.startsWith('/chat/');
  const showModelSelector = isChatRoute || isAgentRoute;
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Fechar dropdown ao clicar fora
  useEffect(() => {
    if (!modelDropdownOpen.value) return;
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        modelDropdownOpen.value = false;
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [modelDropdownOpen.value]);

  // Título dinâmico
  const getTitle = () => {
    const appName = tenantBranding.value?.name || '';
    if (isChatRoute) return selectedAgent?.value?.name || appName;
    if (currentPath === '/' || currentPath === '/hub') return 'Dashboard';
    if (currentPath.startsWith('/agents')) return 'Agentes';
    if (currentPath.startsWith('/integrations')) return 'Integrações';
    if (currentPath.startsWith('/referral')) return 'Indicar';
    if (currentPath.startsWith('/profile')) return 'Perfil';
    if (currentPath.startsWith('/admin')) return 'Admin';
    if (currentPath.startsWith('/upgrade')) return 'Upgrade';
    if (currentPath.startsWith('/vault')) return 'Vault';
    if (currentPath.startsWith('/ambientes')) return 'Ambientes';
    return appName;
  };

  const selectedModelObj = models.value.find(m => m.id === selectedModel.value);

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, height: 48, zIndex: 900,
      background: T.bg,
      borderBottom: `1px solid ${T.whiteAlpha8}`,
      display: 'flex', alignItems: 'center', padding: '0 12px', gap: 10,
    }}>
      {/* ☰ Hamburger — sempre presente */}
      <Button variant="ghost" onClick={onHamburgerClick} style={{
        background: 'none', border: 'none', cursor: 'pointer', padding: 4, flexShrink: 0,
      }}>
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#ECECEC" strokeWidth="2" strokeLinecap="round">
          <line x1="3" y1="8" x2="21" y2="8" />
          <line x1="3" y1="16" x2="21" y2="16" />
        </svg>
      </Button>

      {/* Título + modelo (se aplicável) */}
      <div ref={dropdownRef} style={{ position: 'relative', flex: 1, minWidth: 0 }}>
        <Button variant="ghost" onClick={() => { if (showModelSelector) modelDropdownOpen.value = !modelDropdownOpen.value; }} style={{
          background: 'none', border: 'none', cursor: showModelSelector ? 'pointer' : 'default', padding: 0,
          fontSize: 18, fontWeight: 600, color: '#ECECEC', display: 'flex', alignItems: 'center', gap: 4,
        }}>
          <span style={{ fontWeight: 700, whiteSpace: 'nowrap' }}>{getTitle()}</span>
          {showModelSelector && selectedModelObj && (
            <span style={{ color: T.textMuted, fontWeight: 400, fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {' '}· {selectedModelObj.name}
            </span>
          )}
          {showModelSelector && (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#ECECEC" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
              <polyline points="6 9 12 15 18 9" />
            </svg>
          )}
        </Button>

        {/* Dropdown de modelos */}
        {showModelSelector && modelDropdownOpen.value && (
          <div style={{
            position: 'absolute', top: 'calc(100% + 8px)', left: 0, minWidth: 280,
            background: '#1e1e1e', border: '1px solid #333', borderRadius: 16,
            boxShadow: '0 12px 40px rgba(0,0,0,0.7)', zIndex: 950,
            maxHeight: 400, overflowY: 'auto', overflow: 'hidden',
            animation: 'dropdownIn 0.15s ease-out',
          }}>
            {models.value.map((m, idx) => {
              const isSelected = selectedModel.value === m.id;
              const isFirst = idx === 0;
              const isLast = idx === models.value.length - 1;
              const prevModel = idx > 0 ? models.value[idx - 1] : null;
              const showSeparator = prevModel && prevModel.provider !== m.provider;

              return (
                <div key={m.id}>
                  {showSeparator && <div style={{ height: 1, background: 'rgba(255,255,255,0.06)', margin: '4px 0' }} />}
                  <Button variant="ghost"
                    onClick={() => { selectedModel.value = m.id; modelDropdownOpen.value = false; }}
                    style={{
                      display: 'flex', alignItems: 'center', width: '100%', padding: '12px 16px', border: 'none',
                      background: isSelected ? '#2a2a2a' : '#1e1e1e',
                      color: '#ECECEC', cursor: 'pointer', textAlign: 'left', gap: 12,
                      borderRadius: isFirst ? '16px 16px 0 0' : isLast ? '0 0 16px 16px' : 0,
                      transition: 'background 0.15s',
                    }}
                    onMouseEnter={(e: any) => { e.currentTarget.style.background = '#2a2a2a'; }}
                    onMouseLeave={(e: any) => { e.currentTarget.style.background = isSelected ? '#2a2a2a' : '#1e1e1e'; }}
                  >
                    <div style={{
                      width: 32, height: 32, borderRadius: 8, flexShrink: 0,
                      background: 'rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      {m.provider === 'OpenAI' ? (
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#ECECEC" strokeWidth="1.5"><circle cx="12" cy="12" r="10"/><path d="M12 6v12M6 12h12"/></svg>
                      ) : m.provider === 'Anthropic' ? (
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#ECECEC" strokeWidth="1.5"><path d="M12 2L2 19h20L12 2z"/></svg>
                      ) : m.provider === 'Google' ? (
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#ECECEC" strokeWidth="1.5"><circle cx="12" cy="12" r="10"/><path d="M2 12h20"/><path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/></svg>
                      ) : (
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#ECECEC" strokeWidth="1.5"><circle cx="12" cy="12" r="3"/><path d="M12 1v4M12 19v4M4.22 4.22l2.83 2.83M16.95 16.95l2.83 2.83M1 12h4M19 12h4M4.22 19.78l2.83-2.83M16.95 7.05l2.83-2.83"/></svg>
                      )}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>{m.name}</div>
                      <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginTop: 2 }}>{m.provider}</div>
                    </div>
                    {isSelected && (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ECECEC" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    )}
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Ícone Perfil — SEMPRE presente */}
      <div onClick={() => route('/profile/wallet')} style={{
        width: 34, height: 34, minWidth: 34, minHeight: 34, borderRadius: '50%', cursor: 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: '#2a2a2a', border: '1.5px solid rgba(255,255,255,0.25)', flexShrink: 0,
        boxSizing: 'border-box',
      }} title="Perfil">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ECECEC" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="12" cy="8" r="4" />
          <path d="M20 21a8 8 0 00-16 0" />
        </svg>
      </div>

      {/* Botão + Novo Chat — só quando tem chat ativo */}
      {hasActiveChat && (
        <div onClick={() => { activeConv.value = null; route('/chat'); }} style={{
          width: 34, height: 34, minWidth: 34, minHeight: 34, borderRadius: '50%', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: '#2a2a2a', border: '1.5px solid rgba(255,255,255,0.25)',
          flexShrink: 0, boxSizing: 'border-box',
        }} title="Novo Chat">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ECECEC" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
            <line x1="12" y1="8" x2="12" y2="14" />
            <line x1="9" y1="11" x2="15" y2="11" />
          </svg>
        </div>
      )}

      <style>{`
        @keyframes dropdownIn {
          from { opacity: 0; transform: translateY(-4px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
